import React from 'react'
import { Link } from 'react-router-dom'
import ReviewSlider from '../../components/ReviewSlider'
import { useSeo } from '../../lib/i18n'

export default function HomeEn() {
  useSeo({
    title: 'Borgs Driving School I/S | Kalundborg Driving Licence Cat. B',
    description: 'Borgs Driving School I/S in Kalundborg area. Professional driving instruction, mandatory package Cat. B, theory lessons and high pass rates.',
    lang: 'en',
    pathname: '/en'
  })

  // Scraped hero car asset or modern high quality driving car
  const heroCarImage = 'https://assets.auxodev.dk/assets/44bca6c4-b08c-481d-a28a-ebff52067c25/hero-ny-118.jpg'
  const modernCarImage = 'https://assets.auxodev.dk/assets/44bca6c4-b08c-481d-a28a-ebff52067c25/modern-danish-driving-school-car.png'

  return (
    <div className="bg-slate-50 min-h-screen text-slate-800">
      
      {/* HERO SECTION */}
      <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50/70 via-white to-slate-50 pt-12 pb-20 lg:pt-20 lg:pb-32 border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* Hero Text Content */}
            <div className="lg:col-span-6 space-y-6 text-center lg:text-left">
              <h1 className="font-header text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.15]">
                Driving licence with peace of mind &amp; high pass rates
              </h1>

              <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Welcome to <strong>Borgs Driving School I/S</strong>. We offer modern teaching, patient instructors, and flexible theory teams. Get your driving licence at a comfortable and confident pace.
              </p>

              {/* CTAs */}
              <div className="pt-4 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <Link
                  to="/en/mandatory-package-prices"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-[#8cc73d] hover:bg-[#7eb832] text-slate-950 font-bold text-base transition-all duration-200 active:scale-[0.98]"
                >
                  <span>Join next team</span>
                  <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                </Link>

                <Link
                  to="/en/mandatory-package-prices"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full bg-white hover:bg-slate-100 text-slate-900 font-bold text-base border-2 border-slate-200 hover:border-emerald-500 transition-all duration-200"
                >
                  <i className="fa-solid fa-tags text-emerald-600" aria-hidden="true"></i>
                  <span>Packages &amp; Prices</span>
                </Link>
              </div>

              {/* Key trust bullets */}
              <div className="pt-6 border-t border-slate-200/80 flex flex-wrap items-center justify-center lg:justify-start gap-6 text-xs sm:text-sm font-semibold text-slate-700">
                <span className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-emerald-600 text-base" aria-hidden="true"></i>
                  29 Theory Lessons
                </span>
                <span className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-emerald-600 text-base" aria-hidden="true"></i>
                  Manoeuvre &amp; Skid Track
                </span>
                <span className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-emerald-600 text-base" aria-hidden="true"></i>
                  Green School Car
                </span>
              </div>

              {/* DKU Member Badge */}
              <div className="pt-5 flex items-center justify-center lg:justify-start gap-4">
                <img
                  src="/_assets/dku-logo.webp"
                  alt="Dansk Kørelærer-Union (DKU) Member"
                  className="h-16 w-16 sm:h-20 sm:w-20 object-contain shrink-0"
                />
                <div className="text-left">
                  <div className="text-sm sm:text-base font-bold text-slate-900">Member of Dansk Kørelærer-Union</div>
                  <div className="text-xs sm:text-sm text-slate-600">Your guarantee for certified, professional driving tuition</div>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="lg:col-span-6 relative p-0 m-0">
              <div className="relative w-full overflow-hidden">
                <img
                  src={heroCarImage}
                  onError={(e) => { e.target.src = modernCarImage }}
                  alt="The famous green driving school car - Borgs Driving School I/S"
                  className="w-full h-[350px] sm:h-[450px] lg:h-[480px] object-cover"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 4 INFO BOXES */}
      <section className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Box 1: Enrolment */}
            <a
              href="https://gondrive.com/api/site/school/96/iframe/3?license=B"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-emerald-900 text-white p-8 border-2 border-emerald-800 transition-all duration-300 flex flex-col justify-between shadow-md hover:shadow-2xl"
            >
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="text-3xl text-white">
                    <i className="fa-solid fa-user-plus" aria-hidden="true"></i>
                  </div>
                </div>
                
                <h3 className="font-header text-2xl font-bold text-white mb-3">
                  Enrolment
                </h3>
                <p className="text-white text-sm leading-relaxed mb-6 transition-colors">
                  You can book your place at the driving school right here. Throughout the entire training, I put each individual student first.
                </p>
              </div>

              <div className="pt-4 border-t border-emerald-800 flex items-center justify-between text-sm font-bold text-white transition-colors">
                <span>Sign up today</span>
                <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
              </div>
            </a>

            {/* Box 2: Prices */}
            <Link
              to="/en/mandatory-package-prices"
              className="group bg-white p-8 border-2 border-slate-200 transition-all duration-300 flex flex-col justify-between shadow-sm hover:shadow-2xl"
            >
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="text-3xl text-emerald-700">
                    <i className="fa-solid fa-tags" aria-hidden="true"></i>
                  </div>
                </div>
                
                <h3 className="font-header text-2xl font-bold text-slate-900 transition-colors mb-3">
                  Prices &amp; Packages
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6 transition-colors">
                  Click through and explore our prices for driving licences — designed for those who want transparent rates and high quality.
                </p>
              </div>

              <div className="pt-4 border-t border-slate-200 flex items-center justify-between text-sm font-bold text-emerald-700 transition-colors">
                <span>See our prices</span>
                <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
              </div>
            </Link>

            {/* Box 3: Regaining licence */}
            <Link
              to="/en/contact"
              className="group bg-white p-8 border-2 border-slate-200 transition-all duration-300 flex flex-col justify-between shadow-sm hover:shadow-2xl"
            >
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="text-3xl text-emerald-700">
                    <i className="fa-solid fa-id-card" aria-hidden="true"></i>
                  </div>
                </div>
                
                <h3 className="font-header text-2xl font-bold text-slate-900 transition-colors mb-3">
                  Licence Recovery
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6 transition-colors">
                  Have you lost your licence? You can easily and discreetly prepare for your controlling driving test and recover your licence.
                </p>
              </div>

              <div className="pt-4 border-t border-slate-200 flex items-center justify-between text-sm font-bold text-emerald-700 transition-colors">
                <span>Read more today</span>
                <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
              </div>
            </Link>

            {/* Box 4: Student portal */}
            <a
              href="https://gondrive.com/portal"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white p-8 border-2 border-slate-200 transition-all duration-300 flex flex-col justify-between shadow-sm hover:shadow-2xl"
            >
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="text-3xl text-emerald-700">
                    <i className="fa-solid fa-right-to-bracket" aria-hidden="true"></i>
                  </div>
                </div>
                
                <h3 className="font-header text-2xl font-bold text-slate-900 transition-colors mb-3">
                  Student Portal Login
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6 transition-colors">
                  Log in to our student portal to get an overview of your schedule, modules, and progress.
                </p>
              </div>

              <div className="pt-4 border-t border-slate-200 flex items-center justify-between text-sm font-bold text-emerald-700 transition-colors">
                <span>Log in here</span>
                <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
              </div>
            </a>

          </div>
        </div>
      </section>

      {/* REVIEWS SLIDER SECTION */}
      <ReviewSlider lang="en" />

      {/* MID-PAGE CALLOUT / QUICK FACTS */}
      <section className="py-20 bg-emerald-800 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            <div>
              <h2 className="font-header text-3xl sm:text-4xl font-extrabold tracking-tight mb-6">
                Safety and quality rather than conveyor-belt teaching
              </h2>
              <p className="text-white text-base leading-relaxed mb-8">
                At Borgs Driving School I/S, we prioritise personal contact and great dialogue. We ensure all students receive the time, patience, and guidance needed to become safe and confident drivers.
              </p>

              <div className="grid grid-cols-2 gap-6">
                <div className="bg-emerald-900/60 p-5 border border-emerald-700/50">
                  <div className="text-3xl font-extrabold font-header text-white mb-1">98%</div>
                  <div className="text-xs text-white">Happy &amp; satisfied students in Western Zealand</div>
                </div>
                <div className="bg-emerald-900/60 p-5 border border-emerald-700/50">
                  <div className="text-3xl font-extrabold font-header text-white mb-1">29+</div>
                  <div className="text-xs text-white">Mandatory theory lessons with thorough follow-up</div>
                </div>
              </div>
            </div>

            <div className="bg-emerald-900 text-white p-8 sm:p-10 shadow-2xl border border-emerald-700/60 space-y-6">
              <h3 className="font-header text-2xl sm:text-3xl font-bold text-white mb-2">
                Ready to get your driving licence?
              </h3>
              <p className="text-white text-sm sm:text-base leading-relaxed">
                Fill out our quick contact form or call us directly on <strong className="text-white underline underline-offset-4 decoration-emerald-400">21 47 01 87</strong> / <strong className="text-white underline underline-offset-4 decoration-emerald-400">23 20 04 25</strong> for registration or questions.
              </p>

              <div className="space-y-4 pt-2">
                <Link
                  to="/en/contact"
                  className="w-full py-4 px-6 rounded-full bg-[#8cc73d] hover:bg-[#7eb832] text-slate-950 font-bold flex items-center justify-center gap-2 text-base transition-all whitespace-nowrap"
                >
                  <span>Go to registration form</span>
                  <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  )
}
